import get from "lodash.get";
import { useEffect, useState } from "react";

import * as Client from "api/client";
import { OUT_OF_STOCK_CART_ZH } from "config/const";
import cloneDeep from "lodash.clonedeep";
import { useAuth } from "providers/Auth";
import { useShop } from "providers/Shop";
import { sendAddToCartGa } from "tracking";
import Context from "./Context";

const Provider = ({ children }) => {
  const { validAuth, auth } = useAuth();
  const { products } = useShop();
  const [fetched, setFetched] = useState(false);
  const [carts, setCarts] = useState([]);
  const [customer, setCustomer] = useState({});
  const [discounts, setDiscounts] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [cartProducts, setCartProducts] = useState([]);

  const getProduct = (id) =>
    products.find((product) => product.id === id) || {};
  const getErrorText = (res) => {
    if (Array.isArray(res))
      return res
        .filter((item) => !!item.message)
        .map((item) => {
          const productName = getProduct(item.productId).name || "";
          const messageMap = {
            [OUT_OF_STOCK_CART_ZH]: `product『${productName}』is out of stock, please remove the item`,
          };
          return messageMap[item.message] || `Fail`;
        })
        .join(", ");
    else if (res && typeof res === "object" && res.message)
      return `『${getProduct(res.productId).name || ""}』 ${res.message}`;
    return;
  };

  function replace(carts) {
    return Client.replaceCart(auth.userId, auth.idToken, carts)
      .then((res) => {
        let errorText = getErrorText(res);

        if (!res.err && !errorText) return fetchCarts();
        return { err: true, errorText, res };
      })
      .finally(() => {
        try {
          carts.forEach((cart) => {
            sendAddToCartGa(auth.userId, cart);
          });
        } catch (error) {
          console.error("ga fail");
        }
      });
  }

  function update(cart) {
    return Client.updateCart(auth.userId, auth.idToken, cart)
      .then((res) => {
        let errorText = getErrorText(res);

        if (!res.err && !errorText) return fetchCarts();
        return { err: true, errorText, res };
      })
      .finally(() => {
        try {
          sendAddToCartGa(auth.userId, cart);
        } catch (error) {
          console.error("ga fail");
        }
      });
  }

  function updateCoupon(coupons) {
    setCoupons(coupons);
    const newDiscount = coupons.reduce(
      (acc, { discount }) => {
        if (!acc.find(({ id }) => Number(id) === Number(discount.id)))
          acc.push(cloneDeep(discount));
        return acc;
      },
      [...discounts],
    );
    setDiscounts(newDiscount);
  }

  function fetchCarts() {
    return Client.getCarts(auth.userId, auth.accessToken, auth.idToken)
      .then(({ carts, coupons, customer, discounts, products }) => {
        const sortCarts = (carts || []).map((cart) => ({
          ...cart,
          rawProduct: products.find(({ id }) => id === cart.productID) || {},
        }));

        setCarts(sortCarts);
        setCustomer(customer);
        setDiscounts(discounts);
        setCoupons(coupons);
        setCartProducts(products);
        return { err: false };
      })
      .finally(() => {
        setFetched(true);
      });
  }

  useEffect(() => {
    if (!validAuth || fetched) return;
    fetchCarts();
  }, [validAuth, auth]);

  const props = {
    carts,
    discounts,
    coupons,
    customer,
    fetched,
    cartProducts,
    customerExtras: get(customer, "extra_info.annotations", {}),
    refresh: fetchCarts,
    replace,
    update,
    updateCoupon,
  };

  return (
    <Context.Provider value={{ ...props }}>
      {fetched ? children : <></>}
    </Context.Provider>
  );
};

export default Provider;
